/**
 * Public Shared Conversation page — #1081 follow-up.
 *
 * Renders the read-only conversation snapshot for a chat share token.
 * The API endpoint `GET /api/v1/shared/:token` is intentionally public
 * (see packages/api/src/routes/v1/chat-sharing.ts #577 — the token IS
 * the auth, no Bearer header required).
 *
 * Without this client route, share URLs like `/shared/<token>` rendered
 * an empty Argus shell because TanStack Router fell back to the
 * authenticated root layout, which then 302'd to `/login` when no
 * accessToken was present. The route at `/shared/$token` is now
 * registered as a public route in `__root.tsx`, so a recipient who
 * does NOT have an account on the deployment can still view the
 * snapshot via the link.
 *
 * Renders messages in a simple read-only transcript. We deliberately
 * do NOT pull in the full Ask Argus chrome (sidebar, composer, tools
 * strip) — a share recipient should see a clean snapshot, not the
 * authenticated authoring surface.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, MessageSquare } from 'lucide-react';

interface SharedMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  citations: unknown;
  created_at: string;
}

interface SharedSnapshot {
  title: string;
  messages: SharedMessage[];
}

export const Route = createFileRoute('/shared/$token')({
  component: SharedConversationPage,
});

function SharedConversationPage() {
  const { token } = Route.useParams();
  const [snapshot, setSnapshot] = useState<SharedSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetch(`/api/v1/shared/${encodeURIComponent(token)}`, {
      method: 'GET',
      // No Authorization header — public endpoint. We MUST NOT send
      // the access token; some browsers will replay it implicitly via
      // a service worker if we don't override the default.
      credentials: 'omit',
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404 || res.status === 410) {
          setError(
            'This share link is no longer available. The owner may have revoked it or it has expired.',
          );
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setError(`Could not load shared conversation (status ${res.status}).`);
          setLoading(false);
          return;
        }
        const body = (await res.json()) as SharedSnapshot;
        setSnapshot(body);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Network error';
        setError(`Could not load shared conversation: ${message}`);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {loading && (
          <div
            className="flex items-center justify-center py-20 text-muted-foreground gap-2"
            data-testid="shared-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading shared conversation…</span>
          </div>
        )}

        {error && !loading && (
          <div
            role="alert"
            data-testid="shared-error"
            className="max-w-md mx-auto rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center"
          >
            <AlertCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
            <p className="text-sm font-medium text-destructive mb-1">
              Shared conversation unavailable
            </p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        )}

        {snapshot && !error && !loading && (
          <article data-testid="shared-snapshot">
            <header className="mb-6 pb-4 border-b border-border/40">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>Shared conversation</span>
              </div>
              <h1 className="text-2xl font-semibold">{snapshot.title}</h1>
              <p className="text-xs text-muted-foreground mt-2">
                {snapshot.messages.length}{' '}
                {snapshot.messages.length === 1 ? 'message' : 'messages'}
              </p>
            </header>

            <div className="space-y-6">
              {snapshot.messages.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  This conversation has no messages yet.
                </p>
              ) : (
                snapshot.messages.map((m, i) => (
                  <SharedMessageBlock key={i} message={m} />
                ))
              )}
            </div>

            <footer className="mt-10 pt-4 border-t border-border/40 text-[11px] text-muted-foreground text-center">
              This is a read-only snapshot. To start your own
              conversation, sign in to ArgusAI.
            </footer>
          </article>
        )}
      </div>
    </div>
  );
}

function SharedMessageBlock({ message }: { message: SharedMessage }) {
  const isUser = message.role === 'user';
  const label =
    message.role === 'user'
      ? 'User'
      : message.role === 'assistant'
        ? 'Assistant'
        : 'System';
  let timestamp = '';
  try {
    timestamp = new Date(message.created_at).toLocaleString();
  } catch {
    timestamp = message.created_at;
  }
  return (
    <div data-role={message.role} className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span
          className={
            isUser
              ? 'font-medium text-foreground'
              : 'font-medium text-primary'
          }
        >
          {label}
        </span>
        {timestamp && <span>· {timestamp}</span>}
      </div>
      <div
        className={
          isUser
            ? 'text-sm whitespace-pre-wrap'
            : 'text-sm whitespace-pre-wrap text-foreground/90'
        }
      >
        {message.content}
      </div>
    </div>
  );
}
